import { useEffect, useRef } from 'preact/hooks'

/**
 * Schedules a callback to run before a SAS URI expires.
 * Refreshes at 80% of the expiry time (e.g., at ~96 min for a 120-min token).
 * Automatically reschedules after each refresh since the callback should
 * update the expiry value, triggering a new timer.
 */
export function useSasUriRefresh(
  expiresInSeconds: number | null | undefined,
  onRefresh: () => void | Promise<void>
) {
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = undefined
    }

    if (!expiresInSeconds || expiresInSeconds <= 0) return

    // Refresh at 80% of expiry time, minimum 30 seconds,
    // but never schedule the refresh after the SAS URI has expired.
    const baseDelaySeconds = Math.max(expiresInSeconds * 0.8, 30)
    const maxSafeDelaySeconds = Math.max(expiresInSeconds - 1, 0)
    const refreshInMs = Math.min(baseDelaySeconds, maxSafeDelaySeconds) * 1000

    const safeRefresh = () => {
      try {
        const result = onRefresh()
        if (result instanceof Promise) {
          result.catch((err) => console.error('SAS URI refresh failed:', err))
        }
      } catch (err) {
        console.error('SAS URI refresh failed:', err)
      }
    }

    // nosemgrep: AIK_ts_eval_nodejs -- passing a function ref, not a string
    timerRef.current = setTimeout(safeRefresh, refreshInMs)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = undefined
      }
    }
  }, [expiresInSeconds, onRefresh])
}
